import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
} from '@angular/core';
import { Store } from '@ngxs/store';
import {
  DropZoneEventThrottlerDirective,
  GanttDateToPositionPxPipe,
  GanttEndingPixelPipe,
  GanttStartingPixelPipe,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import { DragAndDropModeService } from '@wilson/shift-timeline/services';
import {
  ShiftTimelineDataState,
  ShiftTimelineSettingsState,
  ShiftTimelineState,
} from '@wilson/shift-timeline/state';
import {
  addMilliseconds,
  differenceInMilliseconds,
  setSeconds,
} from 'date-fns';
import { firstValueFrom } from 'rxjs';
import { DragAndDropHelperService } from '../../services/drag-and-drop-helper.service';
import { MoveAbsenceHelperService } from './move-absence-helper.service';

@Component({
  selector: 'wilson-move-absence-drop-zone',
  imports: [CommonModule],
  template: `<div
    class="drop-zone"
    [style.left.px]="resourcesColumnWidthPx$ | async"
  ></div>`,
  styleUrls: ['./move-absence-drop-zone.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    GanttDateToPositionPxPipe,
    GanttStartingPixelPipe,
    GanttEndingPixelPipe,
    MoveAbsenceHelperService,
  ],
})
export class MoveAbsenceDropZoneComponent
  extends DropZoneEventThrottlerDirective
  implements OnInit
{
  private readonly store = inject(Store);
  private readonly dragAndDropHelperService = inject(DragAndDropHelperService);
  private readonly dragAndDropModeService = inject(DragAndDropModeService);
  private readonly moveAbsenceHelperService = inject(MoveAbsenceHelperService);
  private readonly ganttDateToPositionPxPipe = inject(
    GanttDateToPositionPxPipe,
  );

  public resourcesColumnWidthPx$ = this.store.select(
    ShiftTimelineSettingsState.resourcesColumnWidthPx,
  );

  async ngOnInit(): Promise<void> {
    await this.moveAbsenceHelperService.setAbsenceWidth();
  }

  protected async onDragOverHandler(offsetX: number): Promise<void> {
    const draggingAbsence = await firstValueFrom(
      this.dragAndDropModeService.draggingAbsence$,
    );
    if (draggingAbsence) {
      const adjustedOffsetX =
        offsetX - this.moveAbsenceHelperService.absenceWidth / 2;
      const absenceStartDate = setSeconds(
        this.dragAndDropHelperService.calculateTimeFromPx(
          adjustedOffsetX,
          await firstValueFrom(this.dragAndDropModeService.snappingStepSize$),
        ),
        0,
      );
      const timeDifference = differenceInMilliseconds(
        absenceStartDate,
        new Date(draggingAbsence.absentFrom),
      );

      const mockAbsence = {
        ...draggingAbsence,
        absentFrom: addMilliseconds(
          new Date(draggingAbsence.absentFrom),
          timeDifference,
        ).toISOString(),
        absentTo: addMilliseconds(
          new Date(draggingAbsence.absentTo),
          timeDifference,
        ).toISOString(),
      };
      await this.moveAbsenceHelperService.onDragOverHandler(mockAbsence);

      const absenceStartDateTime = new Date(mockAbsence.absentFrom);
      const absenceEndDateTime = new Date(mockAbsence.absentTo);

      const currentDemarcatorRange = {
        left: this.ganttDateToPositionPxPipe.transform(absenceStartDateTime, {
          fromRightSide: false,
          timelineWidthPixel: this.store.selectSnapshot(
            ShiftTimelineState.frameLength,
          ),
          interval: this.store.selectSnapshot(ShiftTimelineDataState.timeframe),
        }),
        right: this.ganttDateToPositionPxPipe.transform(absenceEndDateTime, {
          fromRightSide: true,
          timelineWidthPixel: this.store.selectSnapshot(
            ShiftTimelineState.frameLength,
          ),
          interval: this.store.selectSnapshot(ShiftTimelineDataState.timeframe),
        }),
        shiftDuration: differenceInMilliseconds(
          absenceStartDateTime,
          absenceEndDateTime,
        ),
        adjustmentNeeded: false,
      };
      this.dragAndDropModeService.setDisplayMovingDemarcatorLine(true);
      this.dragAndDropModeService.setDraggingItemStartDatetime(
        absenceStartDateTime,
      );
      this.dragAndDropModeService.setDraggingItemEndDatetime(
        absenceEndDateTime,
      );
      this.dragAndDropModeService.setHorizontalDemarcatorRange(
        currentDemarcatorRange,
      );
    }
  }

  protected async onDragLeaveHandler(): Promise<void> {
    await this.moveAbsenceHelperService.onDragLeaveHandler();
    this.dragAndDropModeService.setDisplayMovingDemarcatorLine(false);
    this.dragAndDropModeService.setHorizontalDemarcatorRange({
      left: null,
      right: null,
      shiftDuration: null,
      adjustmentNeeded: true,
    });
  }

  protected async onDropHandler(): Promise<void> {
    await this.moveAbsenceHelperService.onDropHandler();
    this.dragAndDropModeService.setDisplayMovingDemarcatorLine(false);
    this.dragAndDropModeService.setHorizontalDemarcatorRange({
      left: null,
      right: null,
      shiftDuration: null,
      adjustmentNeeded: true,
    });
  }
}
